/* eslint-disable react/no-array-index-key */
/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

// app modules: components
import Carousel from 'components/ContentItems/Carousel';
import ContentTile from 'components/ContentTile';
import Grid from 'components/ContentItems/Grid';

// app modules: hooks
import useWindowSize from 'hooks/useWindowSize';

// app modules: services
import route from 'services/route';

// graphql
import ContentTileFields from 'components/ContentTile/ContentTileFields.graphql';

// assets
import CssScaffolding from 'components/Layout/scaffolding.scss';
import CssContentItems from './ContentItems.scss';

const DEFAULT_LIMIT = 10;
const DEFAULT_COLUMNS = 4;
const DEFAULT_GRID_ROWS = 1;
const DEFAULT_ORDER_BY_DIRECTION = 'desc';
const DEFAULT_MOBILE_VIEW = 'list';

export const ContentItemsQuery = gql`
  query GetContentItems(
    $collectionId: Int
    $isbn: String
    $personId: String
    $ids: [String]
    $offset: Int
    $limit: Int
    $orderBy: RecipeSortOrder
    $orderByDirection: SortOrderDirection
    $shareKey: String
  ) {
    Content(
      collectionId: $collectionId
      shareKey: $shareKey
      isbn: $isbn
      personId: $personId
      ids: $ids
      offset: $offset
      limit: $limit
      orderBy: $orderBy
      orderByDirection: $orderByDirection
      includeAlacarte: true
    ) {
      ...ContentTileFields
    }
  }
  ${ContentTileFields}
`;

function ContentItems(props) {
  const {
    canShowMore,
    collectionId,
    columns,
    format,
    frameId,
    frameWidth,
    gridRows,
    gutters,
    hideActions,
    isbn,
    itemWidth,
    limit,
    linkSigner,
    linkTarget,
    mobileView,
    orderBy,
    orderByDirection,
    personId,
    scroll,
    shareKey,
    watermarked,
  } = props;

  useStyles(CssScaffolding, CssContentItems);
  const rootRef = useRef(null);

  // When used as an embed,
  // send messages to the parent frame so it can adjust the iframe height
  useEffect(() => {
    if (window.parent && frameId && rootRef.current) {
      window.parent.postMessage(
        { eventName: 'setHeight', frameId, height: rootRef.current.clientHeight },
        '*',
      );
    }
  });

  // We need to know the embed width.
  // 1) When running on the server we rely on the 'width' parameter being passed in through the querystring. Our embed JS makes this happen.
  //    Otherwise we fall back to the estimatedWidth, which may be based on the user agent and not take into account if we are displayed at less than 100vw.
  //    (perhaps it would be better to fail and just do a client side render?)
  // 2) When running on the client we can measure the width directly using the useWindowSize hook.
  const estimatedWidth = useSelector(state => state.dimensions.body);
  const { width } = useWindowSize();
  const rootWidth = width || parseFloat(frameWidth) || estimatedWidth;

  const ids = Array.isArray(props.id)
    ? props.id
    : props.id
        ?.split(',')
        .map(id => id.trim())
        .filter(id => !!id);
  const numberOfColumns = parseInt(columns, 10) || DEFAULT_COLUMNS;
  const safeLimit = Math.min(parseInt(limit, 10) || DEFAULT_LIMIT, 20);
  const safeGridRows = parseInt(gridRows, 10) || DEFAULT_GRID_ROWS;
  const safeCanShowMore = canShowMore === 'true' || canShowMore === true;

  // mode can either be carousel or grid, and should be specified through the formt prop.
  // We also allow 'scroll' to be used for backwards compatibility.
  let mode = format;
  if (!mode) {
    // No format was specified. Use the scroll setting.
    const carousel = typeof scroll === 'string' ? scroll === 'true' : scroll;
    mode = carousel ? 'carousel' : 'grid';
  }
  const isCarousel = mode === 'carousel';

  const itemsPerRowBasedOnFit = Math.floor(rootWidth / itemWidth) || DEFAULT_COLUMNS;
  const itemsPerRow = columns ? Math.min(itemsPerRowBasedOnFit, columns) : itemsPerRowBasedOnFit;

  // Fetch the content to display
  const [hasMore, setHasMore] = useState(true);
  const { loading, error, data, fetchMore } = useQuery(ContentItemsQuery, {
    variables: {
      collectionId: parseInt(collectionId, 10) || null,
      ids: props.id ? ids : null,
      isbn,
      limit: isCarousel ? safeLimit : itemsPerRow * safeGridRows,
      offset: 0,
      orderBy: orderBy || (collectionId ? null : 'relevance'),
      orderByDirection: orderByDirection || (collectionId ? 'asc' : DEFAULT_ORDER_BY_DIRECTION),
      personId,
      shareKey,
    },
  });
  const items = data ? data.Content : [];

  if (error) {
    // Better to hide than show errors on other sites
    console.error('ckbk error', error.message);
    return null;
  }
  if (loading) {
    // Should only happen server-side
    return null;
  }

  const handleShowMore = () =>
    fetchMore({
      variables: {
        offset: items.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        setHasMore(fetchMoreResult.Content.length !== 0);
        const isDuplicatePage = fetchMoreResult.Content.some(recipe =>
          prev.Content.some(prevRecipe => prevRecipe.id === recipe.id),
        );
        if (isDuplicatePage) {
          console.warn('Duplicate page of recipes ignored');
          return prev;
        }
        return { ...prev, Content: [...prev.Content, ...fetchMoreResult.Content] };
      },
    });

  let recipes;
  if (props.id && !orderBy) {
    // If no order was specified present the recipe in the order of ids provided.
    // Filter out any that didn't match a recipe.
    recipes = ids.map(id => items.find(recipe => recipe.id === id)).filter(recipe => !!recipe);
  } else {
    // Otherwise use the order returned from the API
    recipes = [...items];
  }

  const tiles = recipes.map((recipe, i) => (
    <div key={`content-item-${recipe.id}-${i}`} className="content-items__item">
      {recipe.placeholder ? null : (
        <ContentTile
          watermarked={watermarked}
          hideActions={hideActions}
          item={recipe}
          mobileView={isCarousel ? 'grid' : mobileView || DEFAULT_MOBILE_VIEW}
          linkUrl={
            typeof linkSigner === 'function'
              ? linkSigner(route.content(recipe.id), route.content(recipe.id, recipe.title))
              : null
          }
          linkTarget={linkTarget}
          onClick={() => !linkTarget} // Prevent client side routing when clicking links if a target is set
        />
      )}
    </div>
  ));

  if (isCarousel) {
    return (
      <Carousel
        ref={rootRef}
        className={classNames('content-items', 'content-items--carousel', {
          'content-items--gutters': gutters,
        })}
        width={rootWidth}
        columns={numberOfColumns}
      >
        {tiles}
      </Carousel>
    );
  }
  return (
    <Grid
      className="content-items content-items--grid"
      columns={columns ? parseInt(columns, 10) : null}
      showMore={
        safeCanShowMore && hasMore && items.length && (!ids.length || ids.length > items.length)
      }
      itemWidth={itemWidth}
      mobileClassName="content-items content-items--list"
      mobileView={mobileView || DEFAULT_MOBILE_VIEW}
      onShowMore={handleShowMore}
      ref={rootRef}
      width={rootWidth}
    >
      {tiles}
    </Grid>
  );
}

ContentItems.propTypes = {
  canShowMore: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  collectionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  columns: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  isbn: PropTypes.string,
  format: PropTypes.oneOf(['carousel', 'grid', '']),
  frameId: PropTypes.string,
  frameWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gridRows: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  gutters: PropTypes.bool,
  hideActions: PropTypes.bool,
  itemWidth: PropTypes.number,
  linkSigner: PropTypes.func,
  linkTarget: PropTypes.string,
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mobileView: PropTypes.oneOf(['grid', 'list', '']),
  orderBy: PropTypes.oneOf(['relevance', 'popularity', 'recentlyadded', 'published', '']),
  orderByDirection: PropTypes.oneOf(['asc', 'desc', '']),
  personId: PropTypes.string,
  scroll: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  shareKey: PropTypes.string,
  watermarked: PropTypes.bool,
};

ContentItems.defaultProps = {
  canShowMore: true,
  collectionId: null,
  columns: null,
  format: null, // default is carousel, but that will be determined from the default scroll value
  frameId: null,
  frameWidth: null,
  gridRows: DEFAULT_GRID_ROWS,
  gutters: true,
  hideActions: true,
  id: '',
  isbn: null,
  itemWidth: 256,
  limit: DEFAULT_LIMIT,
  linkSigner: null,
  linkTarget: null,
  mobileView: DEFAULT_MOBILE_VIEW,
  orderBy: null,
  orderByDirection: null,
  personId: null,
  scroll: true,
  shareKey: null,
  watermarked: true,
};

export default ContentItems;
